/**
 * New Testimonial section
 * - <testimonial-slider>       initialises Swiper (works on page load + theme editor re-renders)
 * - <testimonial-video>        plays the short muted preview only while visible, opens the modal on click
 * - <testimonial-video-modal>  native <dialog> that plays the full video with sound and controls
 */

if (!customElements.get('testimonial-slider')) {
  class TestimonialSlider extends HTMLElement {
    connectedCallback() {
      if (this.swiper || typeof Swiper === 'undefined') return;

      const sectionId = this.dataset.sectionId;
      this.swiper = new Swiper(this.querySelector('.swiper'), {
        slidesPerView: 2.9,
        spaceBetween: 16,
        loop: true,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        navigation: {
          nextEl: this.querySelector(`.next-${sectionId}`),
          prevEl: this.querySelector(`.prev-${sectionId}`),
        },
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
        breakpoints: {
          300: { slidesPerView: 1.2, spaceBetween: 12 },
          640: { slidesPerView: 2.1, spaceBetween: 16 },
          768: { slidesPerView: 2.3, spaceBetween: 16 },
          1250: { slidesPerView: 2.8, spaceBetween: 16 },
          1400: { slidesPerView: 3.2, spaceBetween: 24 },
        },
      });
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('testimonial-slider', TestimonialSlider);
}

if (!customElements.get('testimonial-video')) {
  class TestimonialVideo extends HTMLElement {
    connectedCallback() {
      this.preview = this.querySelector('video');
      this.trigger = this.querySelector('[data-video-trigger]');
      this.isVisible = false;

      this.onClick = this.openModal.bind(this);
      this.trigger?.addEventListener('click', this.onClick);

      if (!this.preview) return;

      // Ensure the property is set too; the attribute alone isn't always honoured for autoplay.
      this.preview.muted = true;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.isVisible ? this.playPreview() : this.pausePreview();
        },
        { rootMargin: '100px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
    }

    playPreview() {
      if (!this.preview || this.modalOpen) return;
      const playPromise = this.preview.play();
      // Autoplay can be blocked (e.g. low-power mode); the poster stays visible in that case.
      if (playPromise) playPromise.catch(() => {});
    }

    pausePreview() {
      this.preview?.pause();
    }

    openModal() {
      const modal = document.getElementById(this.dataset.modalId);
      const template = this.querySelector('template');
      if (!modal || !template) return;

      this.modalOpen = true;
      this.pausePreview();

      modal.open(template.content.cloneNode(true), {
        label: this.dataset.title,
        opener: this.trigger,
        onClose: () => {
          this.modalOpen = false;
          if (this.isVisible) this.playPreview();
        },
      });
    }
  }

  customElements.define('testimonial-video', TestimonialVideo);
}

if (!customElements.get('testimonial-video-modal')) {
  class TestimonialVideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-modal-content]');

      this.querySelector('[data-modal-close]')?.addEventListener('click', () => this.close());
      // Fires for Esc and for the close button; backdrop clicks intentionally do nothing.
      this.dialog.addEventListener('close', () => this.onClosed());
    }

    open(fragment, { label, opener, onClose } = {}) {
      this.opener = opener;
      this.onCloseCallback = onClose;

      this.content.replaceChildren(fragment);
      if (label) this.dialog.setAttribute('aria-label', label);

      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');

      const video = this.content.querySelector('video');
      if (video) {
        video.muted = false;
        // Called within the click gesture, so playback with sound is allowed.
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    onClosed() {
      this.content.querySelector('video')?.pause();
      // Removing the video stops any remaining download.
      this.content.replaceChildren();
      document.body.classList.remove('overflow-hidden');

      this.opener?.focus();
      this.onCloseCallback?.();
      this.opener = null;
      this.onCloseCallback = null;
    }
  }

  customElements.define('testimonial-video-modal', TestimonialVideoModal);
}
